import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NetWorthData, Account, CategoryBreakdown, NetWorthHistory } from '../types';
import { accountsAPI, authAPI } from '../services/api';
import api from '../services/api';
import { formatCurrency, convertCurrency, SUPPORTED_CURRENCIES } from '../services/currencyService';
import { useAccountFilter } from '../contexts/AccountFilterContext';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart, PieChart, Pie, Cell, Label,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import ChartCard from '../components/ChartCard';
import ChartTooltip from '../components/ChartTooltip';
import EmptyState from '../components/ui/EmptyState';
import SetupChecklist from '../components/onboarding/SetupChecklist';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

// Map Dashboard dateRange (30, 90, 180, 365, ytd, all) to start/end for cash flow API; matches net worth history range.
const getDateRangeForPeriod = (dateRange: string): { startDate: string; endDate: string } => {
  const end = new Date();
  const endDate = end.toISOString().split('T')[0];
  const start = new Date();
  if (dateRange === 'all') {
    start.setFullYear(start.getFullYear() - 2);
  } else if (dateRange === 'ytd') {
    start.setMonth(0, 1);
  } else {
    start.setDate(start.getDate() - parseInt(dateRange, 10));
  }
  const startDate = start.toISOString().split('T')[0];
  return { startDate, endDate };
};

// Days to request for net worth history: number or 'all'. YTD = days from Jan 1 to today.
const getNetWorthHistoryParam = (dateRange: string): number | 'all' => {
  if (dateRange === 'all') return 'all';
  if (dateRange === 'ytd') {
    const now = new Date();
    const jan1 = new Date(now.getFullYear(), 0, 1);
    return Math.ceil((now.getTime() - jan1.getTime()) / 86400000);
  }
  return parseInt(dateRange, 10);
};

interface CashFlowSummaryMonth {
  month: string;
  income: number;
  expenses: number;
  transfers: number;
  net_cash_flow: number;
  savings_rate: number;
  transaction_count: number;
}

type DashboardView = 'networth-first' | 'balanced';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<NetWorthData | null>(null);
  const [cashFlowSummary, setCashFlowSummary] = useState<CashFlowSummaryMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('180');
  const [userPreferredCurrency, setUserPreferredCurrency] = useState('CAD');
  const [showAssetsLine, setShowAssetsLine] = useState(true);
  const [showLiabilitiesLine, setShowLiabilitiesLine] = useState(true);
  const [dashboardView, setDashboardView] = useState<DashboardView>(() => {
    return (localStorage.getItem('dashboardView') as DashboardView) || 'networth-first';
  });
  const { accountFilter } = useAccountFilter();
  const [hasTransactions, setHasTransactions] = useState(false);
  const [showWelcomeHint, setShowWelcomeHint] = useState(
    () => localStorage.getItem('wealthini_show_welcome_hint') === 'true'
  );

  useEffect(() => {
    let cancelled = false;
    api.transactions.getTransactions().then((res: { transactions?: unknown[] }) => {
      if (!cancelled) setHasTransactions((res?.transactions?.length ?? 0) > 0);
    }).catch(() => { if (!cancelled) setHasTransactions(false); });
    return () => { cancelled = true; };
  }, []);

  const handleViewChange = (view: DashboardView) => {
    setDashboardView(view);
    localStorage.setItem('dashboardView', view);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  // Recalculate filtered data when accountFilter changes
  useEffect(() => {
    // This will trigger a re-render with updated filtered data
    // The filtered calculations are done in the render function
  }, [accountFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Get user preferred currency from profile first so we use it for conversions
      let currency = 'CAD';
      try {
        const profile = await authAPI.getProfile();
        currency = (profile as { default_currency?: string }).default_currency || 'CAD';
        setUserPreferredCurrency(currency);
      } catch (profileErr) {
        console.error('Error fetching profile for currency:', profileErr);
      }

      const { startDate, endDate } = getDateRangeForPeriod(dateRange);

      // Fetch accounts + net worth history and cash flow in parallel
      const [assets, liabilities, assetsBreakdown, liabilitiesBreakdown, netWorthHistory, cashFlowResponse] = await Promise.all([
        accountsAPI.getAccounts({ type: 'asset' }),
        accountsAPI.getAccounts({ type: 'liability' }),
        accountsAPI.getCategoryBreakdown('asset'),
        accountsAPI.getCategoryBreakdown('liability'),
        accountsAPI.getNetWorthHistory(getNetWorthHistoryParam(dateRange)),
        api.transactions.getCashFlowSummary({ startDate, endDate }).catch(() => [] as CashFlowSummaryMonth[])
      ]);

      setCashFlowSummary(Array.isArray(cashFlowResponse) ? cashFlowResponse : []);

      // Convert all account values to user's preferred currency
      const totalAssets = await assets.reduce(async (sumPromise: Promise<number>, account: Account) => {
        const sum = await sumPromise;
        const value = parseFloat(account.current_value?.toString() || '0') || 0;
        const accountCurrency = account.currency || 'CAD';
        const convertedValue = await convertCurrency(value, accountCurrency, currency);
        return sum + convertedValue;
      }, Promise.resolve(0));

      const totalLiabilities = await liabilities.reduce(async (sumPromise: Promise<number>, account: Account) => {
        const sum = await sumPromise;
        const value = parseFloat(account.current_value?.toString() || '0') || 0;
        const accountCurrency = account.currency || 'CAD';
        const convertedValue = await convertCurrency(value, accountCurrency, currency);
        return sum + convertedValue;
      }, Promise.resolve(0));

      const netWorth = totalAssets - totalLiabilities;

      const allAssets = assets;
      const allLiabilities = liabilities;

      setData({
        summary: {
          totalAssets,
          totalLiabilities,
          netWorth
        },
        assetsByCategory: assetsBreakdown,
        liabilitiesByCategory: liabilitiesBreakdown,
        allAssets,
        allLiabilities,
        netWorthHistory
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Filter accounts based on accountFilter
  const getFilteredAccounts = (accounts: Account[]) => {
    if (accountFilter === 'all') return accounts;
    return accounts.filter(account => account.account_type === accountFilter);
  };

  // Calculate filtered totals
  const getFilteredTotals = () => {
    if (!data) return { assets: 0, liabilities: 0, netWorth: 0 };
    
    const filteredAssets = getFilteredAccounts(data.allAssets);
    const filteredLiabilities = getFilteredAccounts(data.allLiabilities);
    
    const totalAssets = filteredAssets.reduce((sum, account) => {
      return sum + (parseFloat(account.current_value?.toString() || '0') || 0);
    }, 0);
    
    const totalLiabilities = filteredLiabilities.reduce((sum, account) => {
      return sum + (parseFloat(account.current_value?.toString() || '0') || 0);
    }, 0);
    
    return {
      assets: totalAssets,
      liabilities: totalLiabilities,
      netWorth: totalAssets - totalLiabilities
    };
  };

  // Calculate filtered insights
  const getFilteredInsights = () => {
    if (!data) return {
      debtToAssetRatio: 0,
      netWorthGrowth: 0,
      emergencyFundRatio: 0,
      investmentRatio: 0,
      monthlySavingsRate: 0
    };
    
    const filteredAssets = getFilteredAccounts(data.allAssets);
    const filteredLiabilities = getFilteredAccounts(data.allLiabilities);
    const filteredTotals = getFilteredTotals();
    
    const debtToAssetRatio = filteredTotals.assets > 0 ? (filteredTotals.liabilities / filteredTotals.assets) * 100 : 0;
    
    // Calculate net worth growth from filtered accounts
    const netWorthGrowth = data.netWorthHistory.length > 1 ? 
      ((data.netWorthHistory[data.netWorthHistory.length - 1].net_worth - data.netWorthHistory[0].net_worth) / Math.abs(data.netWorthHistory[0].net_worth)) * 100 : 0;
    
    // Calculate emergency fund ratio from filtered accounts
    const emergencyFundRatio = filteredTotals.assets > 0 ? 
      (filteredAssets.find((acc: Account) => acc.category?.toLowerCase().includes('savings') || acc.category?.toLowerCase().includes('cash'))?.current_value || 0) / (filteredTotals.liabilities / 12) * 100 : 0;
    
    // Calculate investment ratio from filtered accounts
    const investmentRatio = filteredTotals.assets > 0 ? 
      (filteredAssets.filter((acc: Account) => acc.category?.toLowerCase().includes('investment') || acc.category?.toLowerCase().includes('stock') || acc.category?.toLowerCase().includes('fund')).reduce((sum: number, acc: Account) => sum + (parseFloat(acc.current_value?.toString() || '0') || 0), 0) / filteredTotals.assets) * 100 : 0;
    
    // Calculate monthly savings rate from filtered accounts
    const monthlySavingsRate = data.netWorthHistory.length > 1 ? 
      ((data.netWorthHistory[data.netWorthHistory.length - 1].net_worth - data.netWorthHistory[0].net_worth) / data.netWorthHistory.length) : 0;
    
    return {
      debtToAssetRatio,
      netWorthGrowth,
      emergencyFundRatio,
      investmentRatio,
      monthlySavingsRate
    };
  };

  const getFinancialHealthScore = (i: ReturnType<typeof getFilteredInsights>) => {
    let score = 100;
    if (i.debtToAssetRatio > 70) score -= 30;
    else if (i.debtToAssetRatio > 50) score -= 15;
    if (i.emergencyFundRatio < 3) score -= 20;
    if (i.investmentRatio < 20) score -= 15;
    if (i.monthlySavingsRate < 0) score -= 20;
    score = Math.max(0, score);
    const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Needs attention';
    return { score, label };
  };

  // Calculate filtered category breakdowns
  const getFilteredCategoryBreakdowns = () => {
    if (!data) return { assetsByCategory: [], liabilitiesByCategory: [] };
    
    const filteredAssets = getFilteredAccounts(data.allAssets);
    const filteredLiabilities = getFilteredAccounts(data.allLiabilities);
    
    // Calculate assets by category
    const assetsByCategory: { [key: string]: number } = {};
    filteredAssets.forEach(account => {
      const category = account.category;
      const value = parseFloat(account.current_value?.toString() || '0') || 0;
      assetsByCategory[category] = (assetsByCategory[category] || 0) + value;
    });
    
    // Calculate liabilities by category
    const liabilitiesByCategory: { [key: string]: number } = {};
    filteredLiabilities.forEach(account => {
      const category = account.category;
      const value = parseFloat(account.current_value?.toString() || '0') || 0;
      liabilitiesByCategory[category] = (liabilitiesByCategory[category] || 0) + value;
    });
    
    // Convert to arrays and calculate percentages
    const totalAssets = Object.values(assetsByCategory).reduce((sum, value) => sum + value, 0);
    const totalLiabilities = Object.values(liabilitiesByCategory).reduce((sum, value) => sum + value, 0);
    
    const assetsBreakdown = Object.entries(assetsByCategory).map(([category, value]) => ({
      category,
      total_value: value,
      percentage: totalAssets > 0 ? (value / totalAssets) * 100 : 0,
      count: filteredAssets.filter(acc => acc.category === category).length
    }));
    
    const liabilitiesBreakdown = Object.entries(liabilitiesByCategory).map(([category, value]) => ({
      category,
      total_value: value,
      percentage: totalLiabilities > 0 ? (value / totalLiabilities) * 100 : 0,
      count: filteredLiabilities.filter(acc => acc.category === category).length
    }));
    
    return {
      assetsByCategory: assetsBreakdown.sort((a, b) => b.total_value - a.total_value),
      liabilitiesByCategory: liabilitiesBreakdown.sort((a, b) => b.total_value - a.total_value)
    };
  };

  // Calculate filtered net worth history
  const getFilteredNetWorthHistory = () => {
    if (!data) return [];
    
    // For now, we'll use the existing net worth history but adjust the current values
    // based on the filtered accounts. In a real implementation, you'd want to
    // calculate historical data for filtered accounts specifically.
    const currentFilteredTotals = getFilteredTotals();
    const originalTotals = {
      assets: data.summary.totalAssets,
      liabilities: data.summary.totalLiabilities,
      netWorth: data.summary.netWorth
    };
    
    // Calculate scaling factors
    const assetScale = originalTotals.assets > 0 ? currentFilteredTotals.assets / originalTotals.assets : 0;
    const liabilityScale = originalTotals.liabilities > 0 ? currentFilteredTotals.liabilities / originalTotals.liabilities : 0;
    
    return data.netWorthHistory.map(item => ({
      date: item.date,
      total_assets: item.total_assets * assetScale,
      total_liabilities: item.total_liabilities * liabilityScale,
      net_worth: (item.total_assets * assetScale) - (item.total_liabilities * liabilityScale)
    }));
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-[1600px] mx-auto px-4 py-4 md:px-6">
        {/* Net worth hero skeleton */}
        <div className="mb-8">
          <div className="h-4 w-24 rounded bg-white/5 animate-pulse mb-3" />
          <div className="h-14 w-64 rounded-lg bg-white/5 animate-pulse mb-2" />
          <div className="h-5 w-32 rounded-full bg-white/5 animate-pulse" />
        </div>
        {/* Summary cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-purple-500/10 bg-white/[0.03] p-6 animate-pulse">
              <div className="h-3 w-20 rounded bg-white/5 mb-4" />
              <div className="h-8 w-36 rounded bg-white/5 mb-2" />
              <div className="h-3 w-24 rounded bg-white/5" />
            </div>
          ))}
        </div>
        {/* Chart skeleton */}
        <div className="rounded-2xl border border-purple-500/10 bg-white/[0.03] p-6 animate-pulse">
          <div className="h-4 w-32 rounded bg-white/5 mb-6" />
          <div className="h-64 w-full rounded-lg bg-white/5" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-[1600px] mx-auto px-4 py-4 md:px-6 bg-background">
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-destructive mb-4">{error}</div>
            <Button onClick={fetchData}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1 w-full max-w-[1600px] mx-auto px-4 py-4 md:px-6 bg-background">
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            No data available
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, assetsByCategory, liabilitiesByCategory, netWorthHistory, allAssets, allLiabilities } = data;
  const accounts = [...allAssets, ...allLiabilities];
  const hasLinkedBank = accounts.some((a) => a.plaid_account_id != null || a.linked_account_id != null);
  const filteredTotals = getFilteredTotals();
  const filteredInsights = getFilteredInsights();
  const filteredCategoryBreakdowns = getFilteredCategoryBreakdowns();
  const filteredNetWorthHistory = getFilteredNetWorthHistory();

  // Calculate period change based on selected time frame
  const getPeriodChange = () => {
    if (filteredNetWorthHistory.length < 2) return { 
      assets: { percentage: 0, dollar: 0 }, 
      liabilities: { percentage: 0, dollar: 0 }, 
      netWorth: { percentage: 0, dollar: 0 } 
    };
    
    const current = filteredNetWorthHistory[filteredNetWorthHistory.length - 1];
    
    // For time-based periods, compare with the first data point in the range
    // For "all", compare with the first data point overall
    let previous;
    if (dateRange === 'all') {
      previous = filteredNetWorthHistory[0];
    } else {
      // For specific periods, use the first data point in the filtered range
      previous = filteredNetWorthHistory[0];
    }
    
    const assetsDollar = current.total_assets - previous.total_assets;
    const liabilitiesDollar = current.total_liabilities - previous.total_liabilities;
    const netWorthDollar = current.net_worth - previous.net_worth;
    
    return {
      assets: {
        percentage: previous.total_assets ? (assetsDollar / previous.total_assets) * 100 : 0,
        dollar: assetsDollar
      },
      liabilities: {
        percentage: previous.total_liabilities ? (liabilitiesDollar / previous.total_liabilities) * 100 : 0,
        dollar: liabilitiesDollar
      },
      netWorth: {
        percentage: previous.net_worth ? (netWorthDollar / Math.abs(previous.net_worth)) * 100 : 0,
        dollar: netWorthDollar
      }
    };
  };

  const periodChange = getPeriodChange();
  
  // Get the time frame label for display
  const getTimeFrameLabel = () => {
    switch (dateRange) {
      case '30': return '30D';
      case '90': return '90D';
      case '180': return '6M';
      case '365': return '1Y';
      case 'ytd': return 'YTD';
      case 'all': return 'All';
      default: return '90D';
    }
  };

  // Prepare chart data — use Unix timestamps for the X axis so point spacing
  // reflects actual elapsed time rather than treating every snapshot as equidistant.
  const chartData = filteredNetWorthHistory.map(item => ({
    date: new Date(item.date).getTime(),
    assets: item.total_assets,
    liabilities: item.total_liabilities,
    netWorth: item.net_worth
  }));

  const chartDateFormatter = (ts: number) =>
    new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const hasNetWorthTrendData = chartData.length > 0 && chartData.some(d => d.netWorth !== 0 || d.assets !== 0 || d.liabilities !== 0);

  // Use filtered insights instead of original insights
  const insights = filteredInsights;
  const healthScore = getFinancialHealthScore(insights);

  // Cash flow period totals (same timeframe as toggle)
  const cashFlowTotals = (() => {
    if (!cashFlowSummary.length) return { income: 0, expenses: 0, net: 0, savingsRate: 0 };
    const income = cashFlowSummary.reduce((s, m) => s + (m.income || 0), 0);
    const expenses = cashFlowSummary.reduce((s, m) => s + Math.abs(m.expenses || 0), 0);
    const net = income - expenses;
    const savingsRate = income > 0 ? (net / income) * 100 : 0;
    return { income, expenses, net, savingsRate };
  })();

  // Average monthly net cash flow for the selected period
  const avgMonthlyNet = cashFlowSummary.length > 0 ? cashFlowTotals.net / cashFlowSummary.length : 0;

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  const formatShortCurrency = (value: number): string => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 100_000) return `$${Math.round(value / 1_000)}K`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${Math.round(value)}`;
  };

  // Calculate Y-axis domain based on which series are visible.
  // Never forces a 0 baseline — always tight around visible data.
  // Net-worth-only mode adds extra space below the minimum so the gradient
  // fill has consistent depth across the full chart width.
  const getYAxisDomain = () => {
    if (chartData.length === 0) return [0, 1000];
    const visibleValues = chartData.flatMap(item => {
      const vals: number[] = [item.netWorth];
      if (showAssetsLine) vals.push(item.assets);
      if (showLiabilitiesLine) vals.push(item.liabilities);
      return vals;
    });
    const minValue = Math.min(...visibleValues);
    const maxValue = Math.max(...visibleValues);
    const range = maxValue - minValue || 1;
    const padding = range * 0.1;
    // When only net worth is shown, extend the bottom by 1× the data range.
    // This ensures the area fill under the line is always visible even when
    // the line starts near the bottom of its range.
    const extraBottom = (!showAssetsLine && !showLiabilitiesLine) ? range * 0.5 : 0;
    return [minValue - padding - extraBottom, maxValue + padding];
  };

  // Cash flow chart data: 0 = break even; income above 0, expenses below 0 (negative), net = surplus/deficit
  const cashFlowChartData = (() => {
    const parseMonth = (m: CashFlowSummaryMonth): Date => {
      const raw = m.month;
      if (typeof raw === 'string' && /^\d{4}-\d{2}$/.test(raw)) return new Date(raw + '-01');
      const d = new Date(raw);
      return isNaN(d.getTime()) ? new Date() : d;
    };
    return [...cashFlowSummary]
      .sort((a, b) => parseMonth(a).getTime() - parseMonth(b).getTime())
      .map(m => {
        const d = parseMonth(m);
        const income = m.income || 0;
        const expensesAbs = Math.abs(m.expenses || 0);
        const net = m.net_cash_flow ?? income - expensesAbs;
        return {
          monthLabel: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          monthKey: d.getTime(),
          income,
          expenses: -expensesAbs, // below 0 so chart is centered on break-even
          net
        };
      });
  })();

  const dismissWelcomeHint = () => {
    localStorage.removeItem('wealthini_show_welcome_hint');
    setShowWelcomeHint(false);
  };

  const renderDonutCard = (
    title: string,
    entries: Array<{ category: string; total_value: number; percentage: number }>,
    emptyState: React.ReactNode
  ) => {
    if (entries.length === 0) return (
      <ChartCard title={title}>
        {emptyState}
      </ChartCard>
    );

    const total = entries.reduce((s, e) => s + e.total_value, 0);
    const CHART_HEIGHT = 340;

    type LabelProps = {
      cx?: number; cy?: number;
      midAngle?: number;
      outerRadius?: number;
      index?: number;
      percent?: number;
      name?: string;
    };

    const LABEL_MAX_CHARS = 18;
    const truncate = (s: string) => s.length > LABEL_MAX_CHARS ? s.slice(0, LABEL_MAX_CHARS - 1) + '…' : s;

    const renderLabel = ({ cx: lcx = 0, cy: lcy = 0, midAngle = 0, outerRadius = 0, index = 0, percent = 0, name = '' }: LabelProps) => {
      if (percent < 0.08) return null;
      const RADIAN = Math.PI / 180;
      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);
      const sx = lcx + (outerRadius + 6) * cos;
      const sy = lcy + (outerRadius + 6) * sin;
      const mx = lcx + (outerRadius + 22) * cos;
      const my = lcy + (outerRadius + 22) * sin;
      const ex = mx + (cos >= 0 ? 1 : -1) * 18;
      const ey = my;
      const color = COLORS[index % COLORS.length];
      const isRight = cos >= 0;
      const displayName = truncate(name);
      const dollarValue = formatCurrency(entries[index]?.total_value ?? 0, userPreferredCurrency);
      const labelText = `${displayName}: ${(percent * 100).toFixed(1)}%`;
      const estimatedWidth = Math.max(labelText.length, dollarValue.length) * 9;
      const tx = isRight ? ex + 5 : ex - 5 - estimatedWidth;

      return (
        <g key={index}>
          <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={color} fill="none" strokeWidth={1.2} />
          <circle cx={ex} cy={ey} r={2} fill={color} />
          <text x={tx} y={ey - 5} textAnchor="start" fill="#cbd5e1" fontSize={15} fontWeight={600}>{displayName}: <tspan fill={color} fontWeight={700}>{(percent * 100).toFixed(1)}%</tspan></text>
          <text x={tx} y={ey + 14} textAnchor="start" fill="#e2e8f0" fontSize={14} fontWeight={500}>{dollarValue}</text>
        </g>
      );
    };

    return (
      <div className="chart-card" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="chart-header">
          <h3>{title}</h3>
        </div>
        {/* overflow:visible lets labels render outside the SVG bounds into card padding */}
        <style>{`.donut-chart-wrap svg { overflow: visible !important; }`}</style>
        <div className="donut-chart-wrap">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <Pie
              data={entries}
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={110}
              dataKey="total_value"
              nameKey="category"
              strokeWidth={2}
              stroke="#0f1419"
              label={renderLabel}
              labelLine={false}
              isAnimationActive={true}
            >
              {entries.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
              <Label
                position="center"
                content={({ viewBox }) => {
                  const { cx: vcx = 0, cy: vcy = 0 } = (viewBox ?? {}) as { cx?: number; cy?: number };
                  return (
                    <g>
                      <text x={vcx} y={vcy - 7} textAnchor="middle" dominantBaseline="middle" fill="#f8fafc" fontSize={20} fontWeight={700} letterSpacing="-0.5">
                        {formatShortCurrency(total)}
                      </text>
                      <text x={vcx} y={vcy + 13} textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize={10} letterSpacing="1">
                        TOTAL
                      </text>
                    </g>
                  );
                }}
              />
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload as { category?: string; total_value?: number; percentage?: number } | undefined;
                if (!p) return null;
                const idx = entries.findIndex(e => e.category === p.category);
                const color = COLORS[idx % COLORS.length];
                return (
                  <div style={{ backgroundColor: 'var(--background-card)', border: `1px solid ${color}55`, borderRadius: '8px', padding: '10px 14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#cbd5e1', marginBottom: '3px' }}>
                      {p.category}: <span style={{ color, fontWeight: 700 }}>{(p.percentage ?? 0).toFixed(1)}%</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 500 }}>
                      {formatCurrency(p.total_value ?? 0, userPreferredCurrency)}
                    </div>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderCategoryPies = () => (
    <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] md:gap-6">
      {renderDonutCard(
        'Assets by Category',
        filteredCategoryBreakdowns.assetsByCategory,
        <EmptyState icon="🏦" headline="No assets tracked yet" description="Add a bank account, property, vehicle, or business equity to get started." primaryAction={{ label: 'Add an Asset', onClick: () => navigate('/assets') }} />
      )}
      {renderDonutCard(
        'Liabilities by Category',
        filteredCategoryBreakdowns.liabilitiesByCategory,
        <EmptyState icon="💳" headline="Track what you owe" description="Add a mortgage, loan, or credit card for an accurate net worth." primaryAction={{ label: 'Add a Liability', onClick: () => navigate('/liabilities') }} />
      )}
    </div>
  );

  return (
    <div className="flex-1 w-full max-w-[1600px] mx-auto px-4 py-4 md:px-6 bg-background">
      {showWelcomeHint && (
        <div className="mb-6 rounded-xl border border-purple-500/50 bg-purple-600/15 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-white text-base mb-1">
                {data && (data.allAssets.length > 0 || data.allLiabilities.length > 0)
                  ? '👋 Great start — keep building your picture.'
                  : '👋 Welcome to your dashboard.'}
              </p>
              <p className="text-sm text-white/60 mb-3">
                {data && (data.allAssets.length > 0 || data.allLiabilities.length > 0)
                  ? 'The more you add, the more accurate your net worth becomes.'
                  : 'Add your assets and liabilities to see your real net worth.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '+ Add an asset', path: '/assets' },
                  { label: '+ Add a liability', path: '/liabilities' },
                  { label: '+ Connect a bank', path: '/banks' },
                ].map(({ label, path }) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => { dismissWelcomeHint(); navigate(path); }}
                    className="text-sm font-medium text-purple-300 hover:text-white border border-purple-500/40 hover:border-purple-400 bg-purple-500/10 hover:bg-purple-500/20 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={dismissWelcomeHint}
              className="text-white/30 hover:text-white/60 transition-colors text-xl leading-none shrink-0 mt-0.5"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {/* Header with Net Worth and Controls */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start md:gap-8">
          <div className="flex-1 net-worth-display">
            <h1 className="net-worth-label">Net Worth <span className="header-currency">({userPreferredCurrency})</span></h1>
            <div className={`net-worth-value ${filteredTotals.netWorth > 0 ? 'positive' : filteredTotals.netWorth < 0 ? 'negative' : ''}`}>
              {formatCurrency(filteredTotals.netWorth, userPreferredCurrency)}
            </div>
            {periodChange.netWorth.percentage !== 0 && (
              <div className={`net-worth-change ${periodChange.netWorth.percentage >= 0 ? 'positive' : 'negative'}`}>
                {periodChange.netWorth.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.netWorth.percentage).toFixed(1)}% ({formatCurrency(Math.abs(periodChange.netWorth.dollar), userPreferredCurrency)}) {getTimeFrameLabel()}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 items-end md:flex-row md:w-full md:justify-between md:items-center">
            <div className="flex items-center gap-2">
              <label className="dashboard-view-label" htmlFor="dashboard-view-select">View</label>
              <select
                id="dashboard-view-select"
                className="dashboard-view-select"
                value={dashboardView}
                onChange={(e) => handleViewChange(e.target.value as DashboardView)}
              >
                <option value="networth-first">Net Worth first</option>
                <option value="balanced">Net Worth / Cash Flow</option>
              </select>
            </div>

            <div className="flex gap-2 items-center flex-wrap rounded-lg border border-border bg-secondary p-1 md:flex-nowrap">
              {[
                { value: '30', label: '30D' },
                { value: '90', label: '90D' },
                { value: '180', label: '6M' },
                { value: '365', label: '1Y' },
                { value: 'ytd', label: 'YTD' },
                { value: 'all', label: 'All' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant="ghost"
                  size="sm"
                  className={`min-w-[50px] range-btn ${dateRange === option.value ? 'active' : ''}`}
                  onClick={() => setDateRange(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {localStorage.getItem('wealthini_checklist_dismissed') !== 'true' && (
        <SetupChecklist accounts={accounts} />
      )}

      {dashboardView === 'balanced' && (
        <div className="flex flex-col gap-4 mb-6 md:flex-row">
          {/* LEFT: Net Worth panel */}
          <Card className="dashboard-split-panel">
            <div className="dashboard-split-panel-header">
              <div className="dashboard-split-panel-title-row">
                <h2 className="dashboard-split-panel-title">Net Worth</h2>
                <span className="dashboard-split-panel-currency">({userPreferredCurrency})</span>
              </div>
              <div className={`dashboard-split-panel-value ${filteredTotals.netWorth > 0 ? 'positive' : filteredTotals.netWorth < 0 ? 'negative' : ''}`}>
                {formatCurrency(filteredTotals.netWorth, userPreferredCurrency)}
              </div>
              {periodChange.netWorth.percentage !== 0 && (
                <div className={`dashboard-split-panel-change ${periodChange.netWorth.percentage >= 0 ? 'positive' : 'negative'}`}>
                  {periodChange.netWorth.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.netWorth.percentage).toFixed(1)}% ({formatCurrency(Math.abs(periodChange.netWorth.dollar), userPreferredCurrency)}) {getTimeFrameLabel()}
                </div>
              )}
            </div>

            {/* Chart */}
            <div className="chart-header chart-header-net-worth" style={{ marginTop: '1rem' }}>
              <div className="chart-header-top">
                <div className="chart-header-left">
                  <h3>Trend</h3>
                  <span className="chart-header-timeframe">{dateRange === 'all' ? 'All time' : dateRange === 'ytd' ? 'Year to date' : `Last ${dateRange} days`}</span>
                </div>
                <div className="chart-header-toggles">
                  <span className="chart-header-label">Show</span>
                  <Button type="button" variant="ghost" size="sm" className={`chart-series-toggle chart-series-toggle-assets ${showAssetsLine ? 'active' : ''}`} onClick={() => setShowAssetsLine(s => !s)} aria-pressed={showAssetsLine}>
                    <span className="chart-series-toggle-dot" aria-hidden />
                    {showAssetsLine ? 'Assets on' : 'Assets off'}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className={`chart-series-toggle chart-series-toggle-liabilities ${showLiabilitiesLine ? 'active' : ''}`} onClick={() => setShowLiabilitiesLine(s => !s)} aria-pressed={showLiabilitiesLine}>
                    <span className="chart-series-toggle-dot" aria-hidden />
                    {showLiabilitiesLine ? 'Liabilities on' : 'Liabilities off'}
                  </Button>
                </div>
              </div>
              {(chartData.length >= 2) && (periodChange.netWorth.percentage !== 0 || periodChange.assets.percentage !== 0 || periodChange.assets.dollar !== 0 || periodChange.liabilities.percentage !== 0 || periodChange.liabilities.dollar !== 0) && (
                <div className="chart-header-badges">
                  {periodChange.netWorth.percentage !== 0 && (
                    <Badge className="chart-period-badge chart-period-badge-networth">NW {periodChange.netWorth.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.netWorth.percentage).toFixed(1)}%</Badge>
                  )}
                  {showAssetsLine && (periodChange.assets.percentage !== 0 || periodChange.assets.dollar !== 0) && (
                    <Badge className="chart-period-badge chart-period-badge-assets">Assets {periodChange.assets.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.assets.percentage).toFixed(1)}%</Badge>
                  )}
                  {showLiabilitiesLine && (periodChange.liabilities.percentage !== 0 || periodChange.liabilities.dollar !== 0) && (
                    <Badge className="chart-period-badge chart-period-badge-liabilities">Liab {periodChange.liabilities.percentage <= 0 ? '↘' : '↗'} {Math.abs(periodChange.liabilities.percentage).toFixed(1)}%</Badge>
                  )}
                </div>
              )}
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData}>
                  <defs>
                    <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.55} />
                      <stop offset="55%" stopColor="#8b5cf6" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={chartDateFormatter} stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => { if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`; if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`; return `${v.toFixed(0)}`; }} stroke="#64748b" domain={getYAxisDomain()} tick={{ fontSize: 11 }} />
                  <Tooltip content={(props) => <ChartTooltip {...props} formatValue={(v) => formatCurrency(v, userPreferredCurrency)} labelTitle="Date" labelFormatter={(ts) => chartDateFormatter(Number(ts))} />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area type="monotone" dataKey="netWorth" name="Net Worth" stroke="#8b5cf6" fill="url(#netWorthGradient)" strokeWidth={3} animationDuration={300} dot={chartData.length <= 30 ? { r: 2, fill: '#8b5cf6' } : false} activeDot={{ r: 4, fill: '#8b5cf6', strokeWidth: 0 }} />
                  {showAssetsLine && <Line type="monotone" dataKey="assets" name="Assets" stroke="rgba(110,231,183,0.85)" strokeWidth={1.5} strokeDasharray="5 4" dot={chartData.length <= 30 ? { r: 2 } : false} activeDot={{ r: 4 }} animationDuration={300} />}
                  {showLiabilitiesLine && <Line type="monotone" dataKey="liabilities" name="Liabilities" stroke="rgba(252,165,165,0.85)" strokeWidth={1.5} strokeDasharray="5 4" dot={chartData.length <= 30 ? { r: 2 } : false} activeDot={{ r: 4 }} animationDuration={300} />}
                  <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Mini stat row */}
            <div className="dashboard-split-stats">
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Assets</span>
                <span className="dashboard-split-stat-value">{formatCurrency(filteredTotals.assets, userPreferredCurrency)}</span>
                <span className={`dashboard-split-stat-change ${periodChange.assets.percentage >= 0 ? 'positive' : 'negative'}`}>{periodChange.assets.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.assets.percentage).toFixed(1)}%</span>
              </div>
              <div className="dashboard-split-stat-divider" />
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Liabilities</span>
                <span className="dashboard-split-stat-value">{formatCurrency(filteredTotals.liabilities, userPreferredCurrency)}</span>
                <span className={`dashboard-split-stat-change ${periodChange.liabilities.percentage <= 0 ? 'positive' : 'negative'}`}>{periodChange.liabilities.percentage <= 0 ? '↘' : '↗'} {Math.abs(periodChange.liabilities.percentage).toFixed(1)}%</span>
              </div>
              <div className="dashboard-split-stat-divider" />
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Debt Ratio</span>
                <span className={`dashboard-split-stat-value ${filteredInsights.debtToAssetRatio < 50 ? 'positive' : filteredInsights.debtToAssetRatio < 70 ? '' : 'negative'}`}>{filteredInsights.debtToAssetRatio.toFixed(1)}%</span>
                <span className="dashboard-split-stat-change neutral">{filteredInsights.debtToAssetRatio < 50 ? 'Excellent' : filteredInsights.debtToAssetRatio < 70 ? 'Good' : 'High'}</span>
              </div>
            </div>
          </Card>

          {/* RIGHT: Cash Flow panel */}
          <Card className="dashboard-split-panel">
            <div className="dashboard-split-panel-header">
              <div className="dashboard-split-panel-title-row">
                <h2 className="dashboard-split-panel-title">Cash Flow</h2>
                <span className="dashboard-split-panel-currency">({getTimeFrameLabel()})</span>
              </div>
              <div className={`dashboard-split-panel-value ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>
                {cashFlowTotals.net >= 0 ? '+' : ''}{formatCurrency(cashFlowTotals.net, userPreferredCurrency)}
              </div>
              <div className={`dashboard-split-panel-change ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>
                {cashFlowTotals.net >= 0 ? 'Net surplus' : 'Net deficit'} · {cashFlowTotals.income > 0 ? `${cashFlowTotals.savingsRate.toFixed(1)}% savings rate` : 'No income data'}
              </div>
            </div>

            {/* Chart */}
            <div className="chart-header" style={{ marginTop: '1rem' }}>
              <h3>Monthly breakdown</h3>
              <p>{dateRange === 'all' ? 'All time' : dateRange === 'ytd' ? 'Year to date' : `Last ${dateRange} days`} · 0 = break even</p>
            </div>
            <div className="chart-container">
              {cashFlowChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={cashFlowChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="monthLabel" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => { const abs = Math.abs(v); const num = abs >= 1000 ? `${(abs / 1000).toFixed(0)}k` : String(Math.round(abs)); return `${v < 0 ? '−' : ''}${num}`; }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f8fafc' }} cursor={{ stroke: '#64748b', strokeWidth: 1 }} formatter={(value: number, name: string) => [name === 'Expenses' ? formatCurrency(Math.abs(value), userPreferredCurrency) : formatCurrency(value, userPreferredCurrency), name]} labelFormatter={(label) => label} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <ReferenceLine y={0} stroke="#64748b" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} barSize={20} />
                    <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[0, 0, 4, 4]} barSize={20} />
                    <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: '#6366f1' }} activeDot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">
                  <div className="empty-icon">📊</div>
                  <p>No cash flow data for this period</p>
                </div>
              )}
            </div>

            {/* Mini stat row */}
            <div className="dashboard-split-stats">
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Income</span>
                <span className="dashboard-split-stat-value positive">{formatCurrency(cashFlowTotals.income, userPreferredCurrency)}</span>
              </div>
              <div className="dashboard-split-stat-divider" />
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Expenses</span>
                <span className="dashboard-split-stat-value negative">{formatCurrency(cashFlowTotals.expenses, userPreferredCurrency)}</span>
              </div>
              <div className="dashboard-split-stat-divider" />
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Net</span>
                <span className={`dashboard-split-stat-value ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(cashFlowTotals.net, userPreferredCurrency)}</span>
                <span className={`dashboard-split-stat-change ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>{cashFlowTotals.net >= 0 ? 'Surplus' : 'Deficit'}</span>
              </div>
              <div className="dashboard-split-stat-divider" />
              <div className="dashboard-split-stat">
                <span className="dashboard-split-stat-label">Savings Rate</span>
                <span className={`dashboard-split-stat-value ${cashFlowTotals.savingsRate >= 20 ? 'positive' : cashFlowTotals.savingsRate >= 0 ? '' : 'negative'}`}>{cashFlowTotals.income > 0 ? `${cashFlowTotals.savingsRate.toFixed(1)}%` : '—'}</span>
                <span className={`dashboard-split-stat-change ${cashFlowTotals.savingsRate >= 20 ? 'positive' : cashFlowTotals.savingsRate >= 0 ? 'neutral' : 'negative'}`}>{cashFlowTotals.income > 0 ? (cashFlowTotals.savingsRate >= 20 ? 'Strong' : cashFlowTotals.savingsRate >= 0 ? 'OK' : 'Negative') : ''}</span>
              </div>
            </div>
          </Card>
        </div>
      )}


      {/* Net Worth Trend — shown only in networth-first view */}
      {dashboardView === 'networth-first' && <div className="mb-6 md:mb-12">
        <ChartCard
          large
          headerClassName="chart-header-net-worth"
          headerContent={
            <>
              <div className="chart-header-top">
                <div className="chart-header-left">
                  <h3>Net Worth Trend</h3>
                  <span className="chart-header-timeframe">{dateRange === 'all' ? 'All time' : dateRange === 'ytd' ? 'Year to date' : `Last ${dateRange} days`}</span>
                </div>
                <div className="chart-header-toggles">
                  <span className="chart-header-label">Show</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={`chart-series-toggle chart-series-toggle-assets ${showAssetsLine ? 'active' : ''}`}
                    onClick={() => setShowAssetsLine(s => !s)}
                    aria-pressed={showAssetsLine}
                    title={showAssetsLine ? 'Hide Assets line' : 'Show Assets line'}
                  >
                    <span className="chart-series-toggle-dot" aria-hidden />
                    {showAssetsLine ? 'Assets on' : 'Assets off'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={`chart-series-toggle chart-series-toggle-liabilities ${showLiabilitiesLine ? 'active' : ''}`}
                    onClick={() => setShowLiabilitiesLine(s => !s)}
                    aria-pressed={showLiabilitiesLine}
                    title={showLiabilitiesLine ? 'Hide Liabilities line' : 'Show Liabilities line'}
                  >
                    <span className="chart-series-toggle-dot" aria-hidden />
                    {showLiabilitiesLine ? 'Liabilities on' : 'Liabilities off'}
                  </Button>
                </div>
              </div>
              {(chartData.length >= 2) && (periodChange.netWorth.percentage !== 0 || periodChange.assets.percentage !== 0 || periodChange.assets.dollar !== 0 || periodChange.liabilities.percentage !== 0 || periodChange.liabilities.dollar !== 0) && (
                <div className="chart-header-badges">
                  {periodChange.netWorth.percentage !== 0 && (
                    <Badge className="chart-period-badge chart-period-badge-networth">
                      Net worth {periodChange.netWorth.percentage >= 0 ? '↗' : '↘'}{' '}
                      {Math.abs(periodChange.netWorth.percentage).toFixed(1)}%
                      {' '}({formatCurrency(Math.abs(periodChange.netWorth.dollar), userPreferredCurrency)})
                    </Badge>
                  )}
                  {showAssetsLine && (periodChange.assets.percentage !== 0 || periodChange.assets.dollar !== 0) && (
                    <Badge className="chart-period-badge chart-period-badge-assets">
                      Assets {periodChange.assets.percentage >= 0 ? '↗' : '↘'}{' '}
                      {Math.abs(periodChange.assets.percentage).toFixed(1)}%
                      {' '}({formatCurrency(Math.abs(periodChange.assets.dollar), userPreferredCurrency)})
                    </Badge>
                  )}
                  {showLiabilitiesLine && (periodChange.liabilities.percentage !== 0 || periodChange.liabilities.dollar !== 0) && (
                    <Badge className="chart-period-badge chart-period-badge-liabilities">
                      Liabilities {periodChange.liabilities.percentage <= 0 ? '↘' : '↗'}{' '}
                      {Math.abs(periodChange.liabilities.percentage).toFixed(1)}%
                      {' '}({formatCurrency(Math.abs(periodChange.liabilities.dollar), userPreferredCurrency)})
                    </Badge>
                  )}
                </div>
              )}
            </>
          }
          style={{ marginBottom: 0 }}
        >
          {hasNetWorthTrendData ? (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.55} />
                    <stop offset="55%" stopColor="#8b5cf6" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={chartDateFormatter} stroke="#64748b" />
                <YAxis
                  tickFormatter={(value) => {
                    if (Math.abs(value) >= 1000000) return `${userPreferredCurrency} ${(value / 1000000).toFixed(1)}M`;
                    if (Math.abs(value) >= 1000) return `${userPreferredCurrency} ${(value / 1000).toFixed(0)}k`;
                    return `${userPreferredCurrency} ${value.toFixed(0)}`;
                  }}
                  stroke="#64748b"
                  domain={getYAxisDomain()}
                />
                <Tooltip content={(props) => <ChartTooltip {...props} formatValue={(v) => formatCurrency(v, userPreferredCurrency)} labelTitle="Date" labelFormatter={(ts) => chartDateFormatter(Number(ts))} />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="netWorth" name="Net Worth" stroke="#8b5cf6" fill="url(#netWorthGradient)" strokeWidth={3} animationDuration={300} dot={chartData.length <= 30 ? { r: 2, fill: '#8b5cf6' } : false} activeDot={{ r: 4, fill: '#8b5cf6', strokeWidth: 0 }} />
                {showAssetsLine && <Line type="monotone" dataKey="assets" name="Assets" stroke="rgba(110, 231, 183, 0.85)" strokeWidth={1.5} strokeDasharray="5 4" dot={chartData.length <= 30 ? { r: 2, fill: 'rgba(110, 231, 183, 0.9)' } : false} activeDot={{ r: 4 }} animationDuration={300} />}
                {showLiabilitiesLine && <Line type="monotone" dataKey="liabilities" name="Liabilities" stroke="rgba(252, 165, 165, 0.85)" strokeWidth={1.5} strokeDasharray="5 4" dot={chartData.length <= 30 ? { r: 2, fill: 'rgba(252, 165, 165, 0.9)' } : false} activeDot={{ r: 4 }} animationDuration={300} />}
                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              icon="📊"
              headline="Your net worth journey starts here"
              description="Add your first asset to see your number and track it over time."
              primaryAction={{ label: 'Add an Asset', onClick: () => navigate('/assets') }}
              secondaryAction={{ label: 'Connect a Bank', onClick: () => navigate('/banks') }}
            />
          )}
        </ChartCard>
      </div>}

      {/* ── NET WORTH FIRST view content ── */}
      {dashboardView === 'networth-first' ? (
        <>
          {/* Row 1: Net Worth cards */}
          <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] md:gap-6">
            <Card className="summary-card">

              <div className="kpi-card-header">
                <div className="flex items-center gap-2">
                  <div className="card-icon">📈</div>
                  <h3>Assets</h3>
                </div>
                <button type="button" onClick={() => navigate('/assets')} className="text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium">
                  Manage →
                </button>
              </div>
              <div className="card-body">
                <div className="card-value">{formatCurrency(filteredTotals.assets, userPreferredCurrency)}</div>
                <div className={`card-change ${periodChange.assets.percentage >= 0 ? 'positive' : 'negative'}`}>
                  <span>{periodChange.assets.percentage >= 0 ? '↗' : '↘'} {Math.abs(periodChange.assets.percentage).toFixed(1)}%</span>
                  <span>({formatCurrency(Math.abs(periodChange.assets.dollar), userPreferredCurrency)})</span>
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2">
                  <div className="card-icon">💳</div>
                  <h3>Liabilities</h3>
                </div>
                <button type="button" onClick={() => navigate('/liabilities')} className="text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium">
                  Manage →
                </button>
              </div>
              <div className="card-body">
                <div className="card-value">{formatCurrency(filteredTotals.liabilities, userPreferredCurrency)}</div>
                <div className={`card-change ${periodChange.liabilities.percentage <= 0 ? 'positive' : 'negative'}`}>
                  <span>{periodChange.liabilities.percentage <= 0 ? '↘' : '↗'} {Math.abs(periodChange.liabilities.percentage).toFixed(1)}%</span>
                  <span>({formatCurrency(Math.abs(periodChange.liabilities.dollar), userPreferredCurrency)})</span>
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2">
                  <div className="card-icon">🎯</div>
                  <h3>Debt Ratio</h3>
                </div>
                <span
                  className="kpi-info-icon"
                  title="Liabilities ÷ Assets. Shows how much of what you own is financed by debt. Below 50% is healthy; above 80% signals financial stress. Lenders typically want this below 43% for mortgages."
                >ⓘ</span>
              </div>
              <div className="card-body">
                <div className="card-value">{filteredInsights.debtToAssetRatio.toFixed(1)}%</div>
                <div className={`card-change ${filteredInsights.debtToAssetRatio < 50 ? 'positive' : filteredInsights.debtToAssetRatio < 70 ? 'neutral' : 'negative'}`}>
                  <span>{filteredInsights.debtToAssetRatio < 50 ? 'Excellent' : filteredInsights.debtToAssetRatio < 70 ? 'Good' : 'High'}</span>
                </div>
                <div className="kpi-benchmark">Healthy: below 50%</div>
              </div>
            </Card>
          </div>

          {/* Row 2: Category breakdown pie charts */}
          {renderCategoryPies()}

          {/* Cash Flow section header — mirrors the Net Worth header style */}
          <div className="mb-8 pt-6 mt-6 border-t border-border">
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start md:gap-8">
              <div className="flex-1 net-worth-display">
                <h1 className="net-worth-label">Cash Flow <span className="header-currency">({userPreferredCurrency})</span></h1>
                <div className={`net-worth-value ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>
                  {cashFlowTotals.net >= 0 ? '+' : ''}{formatCurrency(cashFlowTotals.net, userPreferredCurrency)}
                </div>
                {cashFlowSummary.length > 0 && (
                  <div className={`net-worth-change ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>
                    Avg {cashFlowTotals.net >= 0 ? '+' : ''}{formatCurrency(avgMonthlyNet, userPreferredCurrency)}/mo
                    {cashFlowTotals.income > 0 && ` · ${cashFlowTotals.savingsRate.toFixed(1)}% savings rate`}
                    {' · '}{getTimeFrameLabel()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cash Flow chart */}
          <div className="mb-0 md:mb-12">
            <ChartCard
              title="Monthly Breakdown"
              subtitle={`${dateRange === 'all' ? 'All time' : dateRange === 'ytd' ? 'Year to date' : `Last ${dateRange} days`} · 0 = break even`}
            >
              {cashFlowChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart data={cashFlowChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="monthLabel" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => { const abs = Math.abs(v); const num = abs >= 1000 ? `${(abs / 1000).toFixed(0)}k` : String(Math.round(abs)); const sign = v < 0 ? '−' : ''; return `${sign}${userPreferredCurrency} ${num}`; }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f8fafc' }} cursor={{ stroke: '#64748b', strokeWidth: 1 }} formatter={(value: number, name: string) => [name === 'Expenses' ? formatCurrency(Math.abs(value), userPreferredCurrency) : formatCurrency(value, userPreferredCurrency), name]} labelFormatter={(label) => label} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <ReferenceLine y={0} stroke="#64748b" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} barSize={24} />
                    <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[0, 0, 4, 4]} barSize={24} />
                    <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} activeDot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart"><div className="empty-icon">📊</div><p>No cash flow data for this period</p></div>
              )}
            </ChartCard>
          </div>

          {/* Cash Flow cards — directly under the chart */}
          <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] md:gap-6">
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2"><div className="card-icon">💰</div><h3>Income</h3></div>
              </div>
              <div className="card-body">
                <div className="card-value">{formatCurrency(cashFlowTotals.income, userPreferredCurrency)}</div>
                <div className="card-change neutral">
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2"><div className="card-icon">📤</div><h3>Expenses</h3></div>
              </div>
              <div className="card-body">
                <div className="card-value">{formatCurrency(cashFlowTotals.expenses, userPreferredCurrency)}</div>
                <div className="card-change neutral">
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2"><div className="card-icon">📊</div><h3>Net Cash Flow</h3></div>
              </div>
              <div className="card-body">
                <div className={`card-value ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(cashFlowTotals.net, userPreferredCurrency)}</div>
                <div className={`card-change ${cashFlowTotals.net >= 0 ? 'positive' : 'negative'}`}>
                  <span>{cashFlowTotals.net >= 0 ? 'Surplus' : 'Deficit'}</span>
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
            <Card className="summary-card">
              <div className="kpi-card-header">
                <div className="flex items-center gap-2"><div className="card-icon">🎯</div><h3>Savings Rate</h3></div>
              </div>
              <div className="card-body">
                <div className="card-value">{cashFlowTotals.income > 0 ? `${cashFlowTotals.savingsRate.toFixed(1)}%` : '—'}</div>
                <div className={`card-change ${cashFlowTotals.savingsRate >= 20 ? 'positive' : cashFlowTotals.savingsRate >= 0 ? 'neutral' : 'negative'}`}>
                  <span>{cashFlowTotals.income > 0 ? (cashFlowTotals.savingsRate >= 20 ? 'Strong' : cashFlowTotals.savingsRate >= 0 ? 'OK' : 'Negative') : 'No income'}</span>
                  <span className="card-change-period">{getTimeFrameLabel()}</span>
                </div>
              </div>
            </Card>
          </div>
        </>
      ) : (
        <>
          {/* ── BALANCED view: category charts only (cards/CF chart live in hero panels above) ── */}
          <div className="mb-12">
            {renderCategoryPies()}
          </div>
        </>
      )}

      {/* Enhanced Financial Insights */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-center">Financial Health</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] md:gap-6">
          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🛡️</div>
              <div className="insight-title">Emergency Fund</div>
              <span className="insight-tooltip-trigger" title="Months of expenses covered by savings/cash accounts. Based on account category names (savings, cash) and total liabilities ÷ 12." aria-label="How this is calculated">?</span>
            </div>
            <div className="insight-value">
              {insights.emergencyFundRatio > 0 ? `${insights.emergencyFundRatio.toFixed(0)} months` : 'Not calculated'}
            </div>
            <div className="insight-description">
              {insights.emergencyFundRatio >= 6 ? 'Excellent coverage' : 
               insights.emergencyFundRatio >= 3 ? 'Good coverage' : 
               insights.emergencyFundRatio > 0 ? 'Consider building more' : 'Add savings accounts to calculate'}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">📈</div>
              <div className="insight-title">Investment Ratio</div>
              <span className="insight-tooltip-trigger" title="Share of total assets in investment-type accounts. Based on account category names (investment, stock, fund)." aria-label="How this is calculated">?</span>
            </div>
            <div className="insight-value">{insights.investmentRatio.toFixed(1)}%</div>
            <div className="insight-description">
              {insights.investmentRatio >= 50 ? 'Strong investment allocation' : 
               insights.investmentRatio >= 20 ? 'Good investment mix' : 
               insights.investmentRatio > 0 ? 'Consider increasing investments' : 'Add investment accounts to track'}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">💰</div>
              <div className="insight-title">Monthly Savings</div>
            </div>
            <div className={`insight-value ${insights.monthlySavingsRate >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(Math.abs(insights.monthlySavingsRate), userPreferredCurrency)}
            </div>
            <div className="insight-description">
              {insights.monthlySavingsRate >= 0 ? 'Average monthly growth' : 'Average monthly decline'}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🎯</div>
              <div className="insight-title">Financial Health Score</div>
            </div>
            <div className="insight-value">
              {healthScore.score}/100
            </div>
            <div className="insight-description">
              {healthScore.label}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard; 